import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, Eye, EyeOff } from "lucide-react";
import { api, coachHeaders } from "../lib/api";

const input = "min-w-0 rounded-xl border border-line bg-ink px-3 py-2 text-chalk outline-none focus:border-lime";
const errMsg = (e) => e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Action impossible";

const RoleSelect = ({ value, onChange, testId }) => (
  <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className={input}>
    <option value="coach">Coach</option>
    <option value="delegue">Délégué</option>
  </select>
);

const ONGLETS = [["infos", "Infos"], ["convoques", "Convocation"], ["reponses", "Réponses"], ["effectif", "Effectif"], ["presences", "Présences"], ["archives", "Convocations passées"]];
const TOUS = ONGLETS.map(([k]) => k);

// Onglets visibles par un coach / délégué (l'admin voit toujours tout).
const OngletsPicker = ({ f, setF, id }) => {
  const actifs = Array.isArray(f.onglets) ? f.onglets : TOUS;
  const basculer = (k) => setF({ ...f, onglets: actifs.includes(k) ? actifs.filter((x) => x !== k) : TOUS.filter((x) => x === k || actifs.includes(x)) });
  return (
    <div data-testid={`staff-onglets-${id}`} className="col-span-2 flex flex-wrap items-center gap-1.5 sm:col-span-4">
      <span className="mr-1 font-mono text-[11px] uppercase tracking-[0.15em] text-dim">Onglets visibles</span>
      {f.admin ? <span className="text-xs text-lime">Tous (admin)</span> : ONGLETS.map(([k, l]) => (
        <button key={k} type="button" aria-pressed={actifs.includes(k)} data-testid={`staff-onglet-${id}-${k}`} onClick={() => basculer(k)}
          className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${actifs.includes(k) ? "border-lime bg-lime/15 text-lime" : "border-line text-dim line-through hover:text-chalk"}`}>{l}</button>
      ))}
    </div>
  );
};

const StaffRow = ({ s, me, onSave, onDelete }) => {
  const [f, setF] = useState(s);
  const [show, setShow] = useState(false);
  const dirty = f.nom !== s.nom || f.role !== s.role || f.code !== s.code || !!f.admin !== !!s.admin || JSON.stringify(f.onglets ?? null) !== JSON.stringify(s.onglets ?? null);
  return (
    <li data-testid="staff-row" className="grid grid-cols-2 gap-2 py-3 sm:grid-cols-[1fr_auto_1fr_auto]">
      <input data-testid={`staff-nom-${s.id}`} value={f.nom} onChange={(e) => setF({ ...f, nom: e.target.value })} className={input} aria-label="Nom" />
      <RoleSelect testId={`staff-role-${s.id}`} value={f.role} onChange={(role) => setF({ ...f, role })} />
      <div className="relative col-span-2 sm:col-span-1">
        <input data-testid={`staff-code-${s.id}`} type={show ? "text" : "password"} autoCapitalize="none" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} className={`${input} w-full pr-10`} aria-label="Code" />
        <button type="button" onClick={() => setShow(!show)} aria-label="Afficher le code" data-testid={`staff-show-${s.id}`} className="absolute right-3 top-1/2 -translate-y-1/2 text-dim hover:text-chalk">
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      <div className="col-span-2 flex items-center justify-end gap-2 sm:col-span-1">
        <label className="mr-auto inline-flex items-center gap-1 text-xs text-dim sm:mr-0" title="Peut lancer les mises à jour du site">
          <input type="checkbox" data-testid={`staff-admin-${s.id}`} checked={!!f.admin} onChange={(e) => setF({ ...f, admin: e.target.checked })} className="h-4 w-4 accent-[#D4AF37]" />Admin
        </label>
        <button data-testid={`staff-save-${s.id}`} disabled={!dirty} onClick={() => onSave(f)} className="inline-flex items-center gap-1 rounded-full bg-lime px-3 py-2 text-sm font-semibold text-ink disabled:opacity-30"><Save className="h-4 w-4" />OK</button>
        {me ? <span className="font-mono text-[11px] uppercase text-lime">Toi</span>
          : <button data-testid={`staff-delete-${s.id}`} onClick={() => onDelete(s)} aria-label="Supprimer" className="p-2 text-dim hover:text-signal"><Trash2 className="h-4 w-4" /></button>}
      </div>
      <OngletsPicker f={f} setF={setF} id={s.id} />
    </li>
  );
};

export const StaffSettings = ({ code, user, onCodeChanged }) => {
  const [list, setList] = useState([]);
  const [allowed, setAllowed] = useState(false);
  const [f, setF] = useState({ nom: "", role: "delegue", code: "" });
  const load = useCallback(() => {
    api.get("/staff", coachHeaders(code)).then((r) => { setList(Array.isArray(r.data) ? r.data : []); setAllowed(true); }).catch(() => setAllowed(false));
  }, [code]);
  useEffect(() => { load(); }, [load]);
  if (!allowed) return null;

  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/staff", f, coachHeaders(code)); setF({ nom: "", role: "delegue", code: "" }); toast.success("Compte ajouté"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const save = async (s) => {
    try {
      await api.put(`/staff/${s.id}`, s, coachHeaders(code));
      toast.success("Compte modifié");
      if (s.id === user?.id) onCodeChanged(s.code.trim().toLowerCase());
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Supprimer le compte de ${s.nom} ?`)) return;
    try { await api.delete(`/staff/${s.id}`, coachHeaders(code)); toast.success("Compte supprimé"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div data-testid="staff-settings" className="rounded-2xl border border-line bg-pitch p-4">
      <h2 className="font-display text-2xl uppercase text-chalk">Réglages · Codes d'accès</h2>
      <p className="mt-1 text-sm text-dim">Ajoute, modifie ou supprime les comptes coach et délégués. Code : 6 caractères minimum. « Admin » = peut lancer les mises à jour du site. Clique sur un onglet pour le montrer ou le cacher à cette personne, puis OK.</p>
      <ul className="mt-2 divide-y divide-line">
        {list.map((s) => <StaffRow key={`${s.id}-${s.code}-${s.nom}-${s.role}-${s.admin}-${(s.onglets ?? []).join()}`} s={s} me={s.id === user?.id} onSave={save} onDelete={remove} />)}
      </ul>
      <form onSubmit={add} data-testid="staff-add-form" className="mt-3 grid grid-cols-2 gap-2 border-t border-line pt-3 sm:grid-cols-[1fr_auto_1fr_auto]">
        <input data-testid="staff-add-nom" placeholder="Nom" value={f.nom} onChange={(e) => setF({ ...f, nom: e.target.value })} className={input} />
        <RoleSelect testId="staff-add-role" value={f.role} onChange={(role) => setF({ ...f, role })} />
        <input data-testid="staff-add-code" placeholder="Code" autoCapitalize="none" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} className={`${input} col-span-2 sm:col-span-1`} />
        <button data-testid="staff-add-button" className="col-span-2 inline-flex items-center justify-center gap-1 rounded-full bg-lime px-4 py-2 font-semibold text-ink sm:col-span-1"><Plus className="h-4 w-4" />Ajouter</button>
      </form>
    </div>
  );
};
